import { Box, Tooltip } from "@mui/material";
import moment, { Moment } from "moment";
import { RejeemIcon, ShopIcon } from "../assets/icons";
import { PartnerUserDetailResponseActivityLevelEnum, PartnerUserDetailResponseDietGoalEnum } from "../openapi/client";

export const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export const mergeByKey = (arr1: Array<any>, arr2: Array<any>, key: string) => {
  const tempArr1 = [...arr1];
  arr2.forEach((item) => {
    const foundIndex = arr1.findIndex((i) => i[key] === item[key]);
    if (foundIndex > -1) {
      tempArr1[foundIndex] = { ...item };
    } else {
      tempArr1.push(item);
    }
  });
  return tempArr1;
};

export const cdnUrl = process.env.REACT_APP_CDN_URL;

export const checkIf = (obj: any) => typeof obj !== "undefined" && obj !== null;
export const checkIfNull = (obj: any) => obj === null;
export const checkUndefined = (obj: any) => typeof obj === "undefined";
export const checkEmpty = (obj: any) => !checkIf(obj) || (typeof obj === "string" && obj.trim() === "");
export const checkEmptyObj = (obj: any) => Object.keys(obj).length === 0;
export const slugify = (text: string) => {
  return text
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");
};
export const findImageByType = (imageObj: any, type: any) => {
  return imageObj?.find((io: any) => io.type === type)?.url;
};

export const findImageId = (imageObj: any, type: any) => {
  return imageObj?.find((io: any) => io.type === type)?.id;
};

export const matchIsNumeric = (text: any) => {
  const isNumber = typeof text === "number";
  const isString = typeof text === "string";
  return isNumber || (isString && text !== "" && !isNaN(Number(text)));
};

export const formatCurrency = (amount: undefined | number | null, defaultValue: string = "-"  ) =>
  checkIf(amount) ? amount!.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " QAR" : defaultValue;

// Get initials from full name
export const getInitials = (fullName: string): string => {
  if (!fullName || typeof fullName !== "string") {
    return "";
  }

  const names = fullName
    .trim()
    .split(/\s+/)
    .filter((name) => name.length > 0);

  if (names.length === 0) {
    return "";
  }

  if (names.length === 1) {
    // Single name - return first letter
    return names[0][0].toUpperCase();
  }

  // Multiple names - return first letter of first and last name
  const firstInitial = names[0][0].toUpperCase();
  const lastInitial = names[names.length - 1][0].toUpperCase();

  return firstInitial + lastInitial;
};

export enum UserRoles {
  PARTNER_ADMIN = "PARTNER_ADMIN",
  PARTNER_EDITOR = "PARTNER_EDITOR",
}

export const defaultFormat = "YYYY-MM-DD";
export const displayFormat = "DD/MM/YYYY";

export const getUserRole = (user: any) => {
  const isPartnerAdmin = user?.roles?.includes(UserRoles.PARTNER_ADMIN);
  const isPartnerEditor = user?.roles?.includes(UserRoles.PARTNER_EDITOR);

  if (isPartnerAdmin) {
    return UserRoles.PARTNER_ADMIN;
  }
  if (isPartnerEditor) {
    return UserRoles.PARTNER_EDITOR;
  }
  return user?.roles?.[0];
};

export const disableDayCheck = (day: Moment) => {
  const higherLimit = moment().add(-9, "years");
  const lowerLimit = moment().add(-99, "years");
  return !day.isBetween(lowerLimit, higherLimit);
};

export const dateIntervalCheckOnwards = (day1: Moment, day2: Moment) => {
  const isAfter = checkIf(day2) && checkIf(day1) && day2.isAfter(day1);
  const isAfterToday = checkIf(day2) && checkIf(day1) && day1.isSameOrAfter(moment()) && day2.isSameOrAfter(moment());
  return isAfter && isAfterToday;
};

export const dateIntervalCheck = (day1: Moment, day2: Moment) => {
  const isAfter = checkIf(day2) && checkIf(day1) && day2.isAfter(day1);
  return isAfter;
};

export const debounce = (fn: Function, timeDelay = 1000) => {
  let fnTimer: NodeJS.Timeout;
  return (...args: any) => {
    clearTimeout(fnTimer);
    fnTimer = setTimeout(() => fn(...args), timeDelay);
  };
};

export const delay = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(null), ms);
  });
};

export const maxFileSize = 204800; // 200KB

export const generateRandom = () => Math.round(10e10 * Math.random());

export const DayList = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

export const translateError = (errorMessage: string) => {
  switch (errorMessage) {
    case "minDate":
      return "Outside of minimum allowed date";
    case "maxDate":
      return "Outside of maximum allowed date";
    case "invalidDate":
      return "Invalid Date";
    case "shouldDisableDate":
      return "Disabled Date";
    default:
      return errorMessage;
  }
};

export const TransactionType = {
  BALANCE_TRANSFER: "Balance Transfer",
  DELIVERY_FEE: "Delivery Fee",
  MANUAL_ADJUSTMENT: "Manual Adjustment",
  MEAL_CHARGE: "Meal Charge",
  PLAN_SALE: "Plan Sale",
  PLATFORM_FEE: "Platform Fee",
  REFUND: "Refund",
  REFUND_TO_CREDIT_CARD: "Refund to Credit Card",
  CONSULTATION: "Consultation",
  IN_BODY_ANALYSIS: "In Body Analysis",
  DISCOUNT: "Discount",
  PROMOTION: "Promotion",
  PROMOTION_EXPIRY: "Promotion Expiry",
};
export const PaymentType = {
  BANK_TRANSFER: "Bank Transfer",
  CASH: "Cash",
  CREDIT_CARD: "Credit Card",
  PAYMENT_PROVIDER: "Payment Provider",
  OTHER: "Other",
};
export const DiscountState = {
  ACTIVE: "Active",
  EXPIRED: "Expired",
  PASSIVE: "Passive",
};

export const DAY_LIST = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

export const ChannelIcon = (channel: string) => {
  let icon;

  const channelTranslation = (channel: string) => {
    const ref = channel.split("-").map((i) => {
      return i.toString().charAt(0).toLocaleUpperCase() + i.toString().slice(1);
    });
    return ref.join(" ");
  };

  switch (channel) {
    case "rejeem":
      // icon = (
      //   <Tooltip title={channelTranslation(channel)} placement="top" arrow>
      //     <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      //       <RejeemIcon width={16} height={16} />
      //     </Box>
      //   </Tooltip>
      // );
      break;
    default:
      icon = (
        <Tooltip title={channelTranslation(channel)} placement="top" arrow>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <ShopIcon width={16} height={16} />
          </Box>
        </Tooltip>
      );
      break;
  }
  return icon;
};

export const dietGoals = {
  [PartnerUserDetailResponseDietGoalEnum.LoseWeight]: "Lose Weight",
  [PartnerUserDetailResponseDietGoalEnum.Maintenance]: "Maintenance",
  [PartnerUserDetailResponseDietGoalEnum.GainWeight]: "Gain Weight",
  [PartnerUserDetailResponseDietGoalEnum.IncreaseMuscles]: "Increase Muscles",
};

export const activityLevels = {
  [PartnerUserDetailResponseActivityLevelEnum.Sedentary]: "Sedentary",
  [PartnerUserDetailResponseActivityLevelEnum.LightlyActive]: "Lightly Active",
  [PartnerUserDetailResponseActivityLevelEnum.ModeratelyActive]: "Moderately Active",
  [PartnerUserDetailResponseActivityLevelEnum.VeryActive]: "Very Active",
  [PartnerUserDetailResponseActivityLevelEnum.ExtremelyActive]: "Extremely Active",
};

export const activityLevelOptions = {
  [PartnerUserDetailResponseActivityLevelEnum.Sedentary]:
    "Little to no exercise, such as a desk job with no additional physical activity",
  [PartnerUserDetailResponseActivityLevelEnum.LightlyActive]: "Light exercise, such as a daily walk, 1-3 days per week",
  [PartnerUserDetailResponseActivityLevelEnum.ModeratelyActive]: "Moderate exercise, 3-5 days per week",
  [PartnerUserDetailResponseActivityLevelEnum.VeryActive]: "Hard exercise, 6-7 days per week",
  [PartnerUserDetailResponseActivityLevelEnum.ExtremelyActive]:
    "Hard daily exercise and a physical job or two times a day training",
};

// GOOGLE MAPS DRAWING API

export const colors = [
  "#e6194b", // red
  "#3cb44b", // green
  "#ffe119", // yellow
  "#0082c8", // blue
  "#f58231", // orange
  "#911eb4", // purple
  "#46f0f0", // cyan
  "#f032e6", // magenta
  "#d2f53c", // lime
  "#fabebe", // pink
  "#008080", // teal
  "#e6beff", // lavender
  "#aa6e28", // brown
  "#fffac8", // light yellow
  "#800000", // maroon
  "#aaffc3", // mint
  "#808000", // olive
  "#ffd8b1", // peach
  "#000080", // navy
  "#808080", // gray
];

export const POLYGON_ID_KEY = "polygonId";
export const SNAP_DISTANCE = 200;
